import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook that animates a number from 0 to targetValue
 * using requestAnimationFrame with an easeOutExpo curve.
 *
 * @param {number} targetValue - The value to count up to
 * @param {number} duration    - Animation duration in ms (default 2000)
 * @param {number} delay       - Delay before animation starts in ms (default 0)
 * @returns {number} The current animated value (integer)
 */
export default function useAnimatedCounter(targetValue, duration = 2000, delay = 0) {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);
  const startTimeRef = useRef(null);
  const hasStartedRef = useRef(false);

  const easeOutExpo = useCallback((t) => {
    return t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
  }, []);

  useEffect(() => {
    if (targetValue === 0) {
      setValue(0);
      return;
    }

    hasStartedRef.current = false;
    startTimeRef.current = null;

    const delayTimer = setTimeout(() => {
      hasStartedRef.current = true;

      const animate = (timestamp) => {
        if (!startTimeRef.current) {
          startTimeRef.current = timestamp;
        }

        const elapsed = timestamp - startTimeRef.current;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeOutExpo(progress);
        const currentValue = Math.round(easedProgress * targetValue);

        setValue(currentValue);

        if (progress < 1) {
          rafRef.current = requestAnimationFrame(animate);
        }
      };

      rafRef.current = requestAnimationFrame(animate);
    }, delay);

    return () => {
      clearTimeout(delayTimer);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [targetValue, duration, delay, easeOutExpo]);

  return value;
}
